import { useMemo } from "react";

import { StatusPayment } from "@/lib/gql/__generated__/graphql";

import { externalLink } from "@/routes/_constants/external-link";

import { EditGuideTooltip } from "@/components/features/items/edit/_shared/ui/components/system/tooltip/edit-guide-tooltip";

import { Label } from "@/components/ui/label/label";

/**
 * CHILLNN サポート「決済（オーソリ・キャプチャ）のタイミングについて」
 * https://support.chillnn.com/faq/host/settlement/276fffe15c57820f8cfe8156dc30380b
 *
 * ベースURLは externalLink.faqSite（dev/prd で自動切替）を使う。
 * パスは Notion ページ ID で構成されるため、日本語タイトルや旧スラッグは使わない。
 */
const PAYMENT_TIMING_FAQ_URL = `${externalLink.faqSite}/faq/host/settlement/276fffe15c57820f8cfe8156dc30380b`;

type Props = {
	statusPayment?: StatusPayment | null;
};

export const PaymentStatusOfManagementStatusCompositions = ({ statusPayment }: Props) => {
	const canShow = useMemo(() => {
		return !!statusPayment;
	}, [ statusPayment ]);

	/**
	 * オーソリ前かどうか
	 */
	const isPreAuthorized = useMemo(() => {
		return statusPayment === StatusPayment.PreAuthorized;
	}, [ statusPayment ]);
	/**
	 * オーソリ取得済みかどうか
	 */
	const isAuthorized = useMemo(() => {
		return statusPayment === StatusPayment.Authorized;
	}, [ statusPayment ]);
	/**
	 * キャプチャ済みかどうか
	 */
	const isCaptured = useMemo(() => {
		return statusPayment === StatusPayment.Captured;
	}, [ statusPayment ]);

	return (
		canShow && (
			<div className="w-full space-y-2">
				<div className="flex items-center gap-2">
					<p>事前決済ステータス</p>

					<EditGuideTooltip
						guide={`
              クレジットカード決済（事前決済）の予約における、オーソリ・キャプチャの進行状況です。
              <br />
              決済タイミングについては <a href="${PAYMENT_TIMING_FAQ_URL}" target="_blank" rel="noopener noreferrer" class="text-link-400 underline-offset-4 hover:underline">こちら</a>をご確認ください。
              <br /><br />
              <b>オーソリ前</b>
              <br />
              カードの与信枠確保（オーソリ）がまだ完了していない状態です。オーソリに失敗している間も、「オーソリ前」のまま表示されます。
              <br /><br />
              <b>オーソリ取得済み</b>
              <br />
              カードの与信枠確保が完了し、チェックイン日の決済キャプチャを待っている状態です。オーソリに失敗した後、ゲストがカード情報を更新してオーソリが取得できた場合も、「オーソリ取得済み」になります。
              <br /><br />
              <b>キャプチャ済み</b>
              <br />
              決済が確定（キャプチャ）済みです。
            `}
					/>
				</div>

				<div className="flex flex-wrap gap-2">
					<Label text="オーソリ前" variant={isPreAuthorized ? "link" : "black"} />
					<Label text="オーソリ取得済み" variant={isAuthorized ? "link" : "black"} />
					<Label text="キャプチャ済み" variant={isCaptured ? "link" : "black"} />
				</div>
			</div>
		)
	);
};
